/**
 * SF-4996: how many web app super admins can see each company, for the Companies list's
 * "Super admins" column.
 *
 * sf-auth-service `GET /users/super-admin-counts?group=servefirst&companyIds=a,b`
 * (admin:users:read) answers `{ counts: { <id>: { list, all } } }`: `list` people have the
 * company on their company list, `all` people see every company. With `group=servefirst`
 * (SF-5020) it counts every active web app super admin who can see the company, with or without an
 * admin app account: the same people `GET /users?group=servefirst&companyId=<id>&status=active`
 * lists, which is the Users page tab (and its default Active status) the cell links to. A
 * switched-off account can't sign in, so it can't see the company and isn't counted. A sign-in
 * service from before SF-5020 ignores `group` and counts only those with an admin app account.
 *
 * One query for the visible page, split into requests of at most 50 companies, never per row.
 * Fails closed: a failed or incomplete batch rejects the whole page, never partial totals or 0.
 */
import { useDataProvider, useOnError } from '@refinedev/core';
import { useQuery } from '@tanstack/react-query';

export const USERS_READ_PERMISSION = 'admin:users:read';
export const SUPER_ADMIN_COUNTS_URL = '/users/super-admin-counts';
/** The Users page's default tab, where the count link lands. */
export const SUPER_ADMIN_COUNTS_GROUP = 'servefirst';

export interface SuperAdminCount {
  list: number;
  all: number;
}

const isCount = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v) && v >= 0;

/** The counts by company id, or null when the answer is not the documented shape. */
export function parseSuperAdminCounts(raw: unknown): Map<string, SuperAdminCount> | null {
  const counts = (raw as { counts?: unknown } | null)?.counts;
  if (!counts || typeof counts !== 'object' || Array.isArray(counts)) return null;
  const parsed = new Map<string, SuperAdminCount>();
  for (const [id, value] of Object.entries(counts as Record<string, unknown>)) {
    const { list, all } = (value ?? {}) as { list?: unknown; all?: unknown };
    if (!isCount(list) || !isCount(all)) return null;
    parsed.set(id.toLowerCase(), { list, all });
  }
  return parsed;
}

/** The address of the Users page filtered to this company, in the form Refine's table reads. */
export const usersWithAccessPath = (companyId: string) =>
  `/users?filters[0][field]=companyId&filters[0][operator]=eq&filters[0][value]=${encodeURIComponent(companyId)}`;

/**
 * Counts for the visible page, including supported 100-row pages. Read only while `enabled`.
 */
export function useSuperAdminCounts(companyIds: string[], enabled: boolean) {
  const getDataProvider = useDataProvider();
  // Failures go to the auth provider like every useCustom read (a dead session logs out).
  const { mutate: checkError } = useOnError();
  const ids = companyIds.join(',');
  return useQuery<Map<string, SuperAdminCount>>({
    queryKey: ['sf-auth', 'super-admin-counts', ids],
    queryFn: async () => {
      const custom = getDataProvider('authDataProvider').custom;
      if (!custom) throw new Error('The data provider has no custom request.');
      const counts = new Map<string, SuperAdminCount>();
      // Both the auth counts route and sf-api access route accept at most 50 ids per request.
      const batchSize = 50;
      for (let start = 0; start < companyIds.length; start += batchSize) {
        const batch = companyIds.slice(start, start + batchSize);
        const { data } = await custom({
          url: SUPER_ADMIN_COUNTS_URL,
          method: 'get',
          query: { group: SUPER_ADMIN_COUNTS_GROUP, companyIds: batch.join(',') },
        });
        const parsed = parseSuperAdminCounts(data);
        if (!parsed || batch.some((id) => !parsed.has(id.toLowerCase()))) {
          throw new Error('The super admin counts answer was not in the expected shape.');
        }
        for (const [id, count] of parsed) counts.set(id, count);
      }
      return counts;
    },
    enabled: enabled && companyIds.length > 0,
    retry: false,
    refetchOnWindowFocus: false,
    // Another page's counts are not this page's; show loading instead.
    keepPreviousData: false,
    onError: (error) => checkError(error),
  });
}
